import React, { useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, Download, Filter, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, GhostButton, ScoreBar } from "@/shared/components/ui-kit";
import { SelectInput } from "@/shared/components/settings-atoms";
import { fmtMs, fmtCost } from "@/shared/lib/runs";
import { CHECK_TYPES, composite, verdict } from "@/shared/lib/evals-model";
import { compareRuns } from "@/shared/lib/evals-engine";
import { ScorePill, DeltaChip, StatusPill } from "./atoms";

import type {
  Agent, EvalCheckType, EvalCompareRow, EvalRun, EvalVerdict,
} from "@/shared/types";

/* Screen 2 — the experiment table. Rows are cases, columns are checks.

   Columns are keyed by check TYPE rather than by check id. Case-local checks
   mean no two cases carry the same id set, so an id-keyed table would be mostly
   holes; a type-keyed one lines up and stays readable, and the per-check detail
   is one click away in the case sheet. Cells with several checks of one type
   show their composite. */

const VERDICT_COPY: Record<EvalVerdict, [label: string, tone: string]> = {
  improvement: ["Improvement", "text-[var(--success)]"],
  regression: ["Regression", "text-[var(--danger)]"],
  tie: ["Tie", "text-[var(--text-tertiary)]"],
  none: ["No baseline", "text-[var(--text-quaternary)]"],
};

function SummaryStrip({ run, base }: { run: EvalRun; base: EvalRun | null }) {
  const v = verdict(run.score, base?.score);
  const [label, tone] = VERDICT_COPY[v];
  const delta = base?.score != null && run.score != null ? +(run.score - base.score).toFixed(3) : null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-1 p-1 rounded-[18px] bg-[var(--surface-2)] mb-5">
      <div className="rounded-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] shadow-lift px-4 py-3.5">
        <div className="section-label">Score</div>
        <div className="text-[18px] font-semibold tracking-[-0.02em] tabular-nums leading-tight mt-1">
          {run.score == null ? "—" : run.score.toFixed(2)}
        </div>
        <div className={`text-[11.5px] mt-[2px] ${tone}`}>
          {label}{delta != null && v !== "tie" && v !== "none" ? ` · ${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(2)}` : ""}
        </div>
      </div>
      <div className="rounded-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] shadow-lift px-4 py-3.5">
        <div className="section-label">Passing</div>
        <div className="text-[18px] font-semibold tracking-[-0.02em] tabular-nums leading-tight mt-1">
          {run.results.filter((r) => r.status === "pass").length}<span className="text-[var(--text-quaternary)]">/{run.results.length}</span>
        </div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[2px]">{Math.round(run.passRate * 100)}% of cases</div>
      </div>
      {/* Cost and duration sit BESIDE the scores, never inside them. No mature
          eval product scores efficiency — an agent taking longer is not the same
          kind of fact as an agent being wrong. */}
      <div className="rounded-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] shadow-lift px-4 py-3.5">
        <div className="section-label">Cost</div>
        <div className="text-[18px] font-semibold tracking-[-0.02em] tabular-nums leading-tight mt-1">{fmtCost(run.cost)}</div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[2px]">across {run.results.length} cases</div>
      </div>
      <div className="rounded-[14px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] shadow-lift px-4 py-3.5">
        <div className="section-label">Wall clock</div>
        <div className="text-[18px] font-semibold tracking-[-0.02em] tabular-nums leading-tight mt-1">{fmtMs(run.ms)}</div>
        <div className="text-[11.5px] text-[var(--text-tertiary)] mt-[2px]">sum of case runs</div>
      </div>
    </div>
  );
}

const COL = "w-[68px] shrink-0 text-center";

/* The run it compares with is the page's: a server run is loaded by id before it
   can be compared, and the page is what asks for it. */
export default function Matrix({ run, runs, agent, caseName, baselineId, compareId, onCompare, onBack, onOpenCase, onSetBaseline }: {
  run: EvalRun;
  runs: EvalRun[];
  agent: Agent;
  /** A case's name — as the suite has it, or as this run pinned it. */
  caseName: (caseId: string) => string;
  baselineId: string | null;
  /** "" compares with nothing. */
  compareId: string;
  onCompare: (runId: string) => void;
  onBack: () => void;
  onOpenCase: (caseId: string, baseId: string | null) => void;
  onSetBaseline: (id: string) => void;
}) {
  const [failuresOnly, setFailuresOnly] = useState(false);
  const [regressionsFirst, setRegressionsFirst] = useState(true);

  const base = runs.find((r) => r.id === compareId) || null;

  /* Union of check types actually present, in the declared order of
     CHECK_TYPES so the column order is stable between runs. */
  const columns = useMemo(() => {
    const present = new Set(run.results.flatMap((r) => r.checks.map((c) => c.type)));
    // The registry's keys ARE the check types, in the order they are declared.
    return (Object.keys(CHECK_TYPES) as EvalCheckType[]).filter((t) => present.has(t));
  }, [run]);

  const cellFor = (result: EvalCompareRow, type: EvalCheckType): number | null =>
    composite(result.checks.filter((c) => c.type === type));

  const rows = useMemo(() => {
    let out = compareRuns(run, base);
    if (failuresOnly) out = out.filter((r) => r.status !== "pass");
    if (regressionsFirst && base) {
      // A regression you cannot find is a regression you will ship. Default the
      // sort to put them at the top rather than making it an opt-in control.
      out = [...out].sort((a, b) => (a.delta ?? 0) - (b.delta ?? 0));
    }
    return out;
  }, [run, base, failuresOnly, regressionsFirst]);

  const exportRun = () => {
    const blob = new Blob([JSON.stringify({ ...run, agent: agent.id }, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${agent.id}-${run.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Eval run exported");
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <PageHeader
        title={
          <>
            <button onClick={onBack} title="Back to suite" className="press shrink-0 w-7 h-7 rounded-[7px] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]">
              <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
            </button>
            <span className="text-[14px] font-semibold tracking-[-0.01em] truncate">{run.label}</span>
            <span className="shrink-0 h-[17px] px-[7px] rounded-full text-[10.5px] font-medium flex items-center bg-[var(--surface-2)] text-[var(--text-secondary)] font-mono">
              {run.target.version}
            </span>
          </>
        }
      >
        <SelectInput
          compact
          className="w-[172px]"
          value={compareId}
          onChange={onCompare}
          options={[
            { id: "", label: "Compare with…" },
            ...runs.filter((r) => r.id !== run.id).map((r) => ({ id: r.id, label: `${r.label} · ${r.when}` })),
          ]}
        />
        {baselineId !== run.id && (run.status ?? "done") === "done" && (
          <GhostButton onClick={() => onSetBaseline(run.id)} title="Every later run is measured against this one">
            Set as baseline
          </GhostButton>
        )}
        <GhostButton onClick={exportRun} title="Download this run as JSON">
          <Download className="w-3.5 h-3.5" strokeWidth={1.75} />
        </GhostButton>
      </PageHeader>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[1160px] mx-auto px-4 md:px-6 py-6 fade-in-up">
          {run.status === "running" && (
            <div role="status" className="mb-4 px-3 py-2.5 rounded-[10px] bg-[var(--info-bg)] ring-1 ring-[var(--info-ring)] text-[12.5px] text-[var(--info-text)]">
              Grading {run.graded ?? 0}/{run.total ?? 0} cases — each result appears as its run ends.
            </div>
          )}
          {run.status === "error" && run.error && (
            <div role="alert" className="mb-4 px-3 py-2.5 rounded-[10px] bg-[var(--danger-bg)] ring-1 ring-[var(--danger-ring)] text-[12.5px] text-[var(--text-primary)]">
              No case produced a score — {run.error}
            </div>
          )}
          <SummaryStrip run={run} base={base} />

          <div className="flex items-center gap-2 flex-wrap pb-4">
            <button
              onClick={() => setFailuresOnly((v) => !v)}
              className={`press h-8 px-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 ${
                failuresOnly
                  ? "bg-[var(--accent-active)] ring-1 ring-[var(--hairline-strong)] text-[var(--text-primary)]"
                  : "ring-1 ring-[var(--hairline-strong)] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
              }`}
            >
              <Filter className="w-3 h-3" strokeWidth={1.75} />
              Failures only
            </button>
            {base && (
              <button
                onClick={() => setRegressionsFirst((v) => !v)}
                className={`press h-8 px-2.5 rounded-full text-[12.5px] font-medium inline-flex items-center gap-1.5 ${
                  regressionsFirst
                    ? "bg-[var(--accent-active)] ring-1 ring-[var(--hairline-strong)] text-[var(--text-primary)]"
                    : "ring-1 ring-[var(--hairline-strong)] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
                }`}
              >
                <TrendingDown className="w-3 h-3" strokeWidth={1.75} />
                Regressions first
              </button>
            )}
            <div className="flex-1" />
            <span className="text-[12px] text-[var(--text-tertiary)]">
              {rows.length} of {run.results.length} cases
            </span>
          </div>

          {/* The table scrolls inside its own container — the page body never
              pans sideways, however many check columns a suite grows. */}
          <div className="tray-box overflow-hidden">
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                <div className="flex items-center gap-3 px-3 pt-2.5 pb-2 border-b hairline">
                  <span className="flex-1 min-w-[200px] section-label">Case</span>
                  <span className="w-[104px] shrink-0 section-label">Score</span>
                  {base && <span className="w-[60px] shrink-0 section-label text-center">Δ</span>}
                  {columns.map((t) => (
                    // Abbreviated so ten columns fit without ellipsing every
                    // header into uselessness; the full name is on hover.
                    <span key={t} className={`${COL} section-label truncate`} title={CHECK_TYPES[t].label}>
                      {CHECK_TYPES[t].short}
                    </span>
                  ))}
                  <span className="hidden md:block w-[56px] shrink-0 section-label text-right">Cost</span>
                  <span className="hidden md:block w-[52px] shrink-0 section-label text-right">Time</span>
                  <span className="w-[70px] shrink-0 section-label text-right">Status</span>
                  <span className="w-3.5 shrink-0" />
                </div>

                <div className="divide-y divide-[var(--hairline)]">
                  {rows.map((r) => (
                    <button
                      key={r.caseId}
                      onClick={() => onOpenCase(r.caseId, base?.id || null)}
                      className="press w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--accent-hover)] transition-colors"
                    >
                      <span className="flex-1 min-w-[200px] text-[13.5px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">
                        {caseName(r.caseId)}
                      </span>
                      <span className="w-[104px] shrink-0">
                        <ScoreBar value={r.score} target={0.7} width={44} />
                      </span>
                      {base && <span className="w-[60px] shrink-0 text-center"><DeltaChip delta={r.delta} /></span>}
                      {columns.map((t) => (
                        <span key={t} className={COL}>
                          <ScorePill score={cellFor(r, t)} />
                        </span>
                      ))}
                      <span className="hidden md:block w-[56px] shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-secondary)] text-right">
                        {fmtCost(r.cost)}
                      </span>
                      <span className="hidden md:block w-[52px] shrink-0 font-mono text-[12px] tabular-nums text-[var(--text-secondary)] text-right">
                        {fmtMs(r.ms)}
                      </span>
                      <span className="w-[70px] shrink-0 text-right"><StatusPill status={r.status} /></span>
                      <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={1.75} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {rows.length === 0 && (
            <div className="py-12 text-center text-[13px] text-[var(--text-tertiary)] tray-box mt-3">
              {run.results.length ? "Every case passed." : "Loading results…"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
